import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { StylesProviderAnywhere } from "@bspk/ui/StylesProviderAnywhere";
import { UIProvider } from "@bspk/ui/UIProvider";
import { BrandProvider } from "./theme/BrandProvider";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { SessionTimeoutProvider } from "./contexts/SessionTimeoutContext";
import { MetroConfigProvider } from "./contexts/MetroConfigContext";
import { ToastContainer } from 'react-toastify';
import App from "./App";
import "./global.css";
import { config } from "./services/AxiosClient";

// Brand can be selected by runtime config or Vite environment; BrandProvider owns the default.
const brandKey = config?.VITE_BRAND_KEY ?? import.meta.env.VITE_BRAND_KEY;

// Boundary that only enables session timeout AFTER authentication succeeds
const AuthenticatedSessionBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <>{children}</>; // No modal/timer until authenticated
  // Production: 45m inactivity, warn at T-15m (defaults in provider). If future product needs change,
  // pass explicit props again: <SessionTimeoutProvider inactivityLimitMs={45*60*1000} warningBeforeMs={15*60*1000}>.
  // TEST CONFIG: 5m total, warn after 3m inactivity (remaining 2m) -> warningBeforeMs = 2m
  // Revert to defaults (remove props) for production (45m / 15m)
  return (
    <SessionTimeoutProvider
      inactivityLimitMs={(config?.VITE_SESSION_TIMEOUT_MINUTES ?? import.meta.env.VITE_SESSION_TIMEOUT_MINUTES ?? 45) * 60 * 1000}
      warningInactivityThresholdMs={(config?.VITE_SESSION_TIMEOUT_ALERT_MINUTES ?? import.meta.env.VITE_SESSION_TIMEOUT_ALERT_MINUTES ?? 30) * 60 * 1000}
      // fallback warningBeforeMs retained but ignored because fixed threshold provided
      warningBeforeMs={15 * 60 * 1000}
    >
      {children}
    </SessionTimeoutProvider>
  );
};

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
  <React.StrictMode>
    <BrowserRouter>
      <StylesProviderAnywhere />
      <UIProvider>
        <MetroConfigProvider>
          <BrandProvider brandKey={brandKey}>
            <AuthProvider>
              <AuthenticatedSessionBoundary>
                <App />
                <ToastContainer />
              </AuthenticatedSessionBoundary>
            </AuthProvider>
          </BrandProvider>
        </MetroConfigProvider>
      </UIProvider>
    </BrowserRouter>
  </React.StrictMode>
);
